/**
 * Support — Sprint D (Feb 2026)
 * POST /api/support/ticket with { category, subject, message }.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, UserCog, CreditCard, MessageSquare, ShieldAlert, HelpCircle, Heart, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';
import { LegalPageShell, LegalFooter } from '@/components/legal/LegalPrimitives';

const CATEGORIES = [
  { key: 'bug',      label: 'Bug Report', icon: Bug },
  { key: 'account',  label: 'Account',    icon: UserCog },
  { key: 'billing',  label: 'Billing',    icon: CreditCard },
  { key: 'feedback', label: 'Feedback',   icon: MessageSquare },
  { key: 'safety',   label: 'Safety',     icon: ShieldAlert },
  { key: 'other',    label: 'Other',      icon: HelpCircle },
];

export default function Support() {
  const navigate = useNavigate();
  const [category, setCategory] = useState('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Please add a subject and message');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/support/ticket', { category, subject: subject.trim(), message: message.trim() });
      toast.success("Thanks — we'll be in touch soon");
      setTimeout(() => navigate('/profile'), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalPageShell title="Support">
      {/* Rainbow heart hero */}
      <div className="flex flex-col items-center text-center py-6 mb-2" data-testid="support-hero">
        <div
          className="w-[68px] h-[68px] rounded-2xl flex items-center justify-center mb-4"
          style={{
            background: 'linear-gradient(135deg, #DC2626 0%, #E11D48 25%, #7E22CE 50%, #2563EB 75%, #1E4FD8 100%)',
            boxShadow: '0 8px 14px 0 rgba(220,38,38,0.4)',
          }}
        >
          <Heart size={30} className="text-white" fill="#FFFFFF" strokeWidth={2}/>
        </div>
        <h2 className="text-[22px] font-[900] text-[#0F0F10]" style={{ letterSpacing: '-0.4px' }}>
          How can we help?
        </h2>
        <p className="text-[13px] text-[#6B7280] font-[600] mt-1.5 max-w-xs">
          We usually respond within 24 hours on weekdays.
        </p>
      </div>

      {/* Category pills */}
      <p className="text-[11px] font-[700] tracking-[0.12em] uppercase text-[#6B7280] mb-2">
        What's this about?
      </p>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="support-categories">
        {CATEGORIES.map(({ key, label, icon: Icon }) => {
          const isSel = category === key;
          return (
            <button
              key={key}
              onClick={() => setCategory(key)}
              data-testid={`support-cat-${key}`}
              className="inline-flex items-center gap-2 rounded-full text-[13px] font-[700] transition-all"
              style={{
                padding: '10px 14px',
                background: isSel
                  ? 'linear-gradient(135deg, #DC2626 0%, #7E22CE 50%, #1E4FD8 100%)'
                  : '#FFFFFF',
                color: isSel ? '#FFFFFF' : '#374151',
                border: isSel ? '1px solid transparent' : '1px solid rgba(15,15,16,0.12)',
                boxShadow: isSel ? '0 4px 10px -2px rgba(220,38,38,0.35)' : 'none',
              }}
            >
              <Icon size={14} strokeWidth={2.2} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Subject */}
      <label className="block text-[11px] font-[700] tracking-[0.12em] uppercase text-[#6B7280] mb-1.5">
        Subject
      </label>
      <input
        data-testid="support-subject-input"
        type="text"
        maxLength={100}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Short description"
        className="w-full rounded-xl px-4 py-3 text-[15px] text-[#0F0F10] mb-4 focus:outline-none focus:ring-2 focus:ring-[#DC2626] transition-all"
        style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.1)' }}
      />

      {/* Message */}
      <label className="block text-[11px] font-[700] tracking-[0.12em] uppercase text-[#6B7280] mb-1.5">
        Message
      </label>
      <textarea
        data-testid="support-message-input"
        rows={6}
        maxLength={2000}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Tell us what's going on..."
        className="w-full rounded-xl px-4 py-3 text-[15px] text-[#0F0F10] mb-1 focus:outline-none focus:ring-2 focus:ring-[#DC2626] transition-all resize-none"
        style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.1)' }}
      />
      <p className="text-[11px] text-[#9CA3AF] text-right mb-5">{message.length}/2000</p>

      <button
        onClick={submit}
        disabled={submitting || !subject.trim() || !message.trim()}
        data-testid="support-submit-btn"
        className="w-full flex items-center justify-center gap-2 rounded-full text-white font-[900] text-[15px] py-3.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #DC2626 0%, #7E22CE 50%, #1E4FD8 100%)',
          boxShadow: '0 6px 20px -4px rgba(220,38,38,0.45)',
          letterSpacing: '-0.2px',
        }}
      >
        <Send size={16} />
        {submitting ? 'Sending…' : 'Send message'}
      </button>

      <LegalFooter />
    </LegalPageShell>
  );
}
